import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (username, password) =>
    api.post('/api/auth/login', { username, password }),
  
  register: (username, email, password, role = 'client') =>
    api.post('/api/auth/register', { username, email, password, role }),
  
  getCurrentUser: () =>
    api.get('/api/auth/me'),
  
  updateProfile: (userData) =>
    api.put('/api/auth/update-profile', userData),
  
  changePassword: (currentPassword, newPassword) =>
    api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// API Tokens API
export const apiTokensAPI = {
  list: () => api.get('/api/api-tokens'),
  create: (payload) => api.post('/api/api-tokens', payload),
  revoke: (tokenId) => api.delete(`/api/api-tokens/${tokenId}`),
};

// Users API
export const usersAPI = {
  getUsers: () =>
    api.get('/api/users'),
  
  createUser: (userData) =>
    api.post('/api/users', userData),
  
  updateUser: (userId, userData) =>
    api.put(`/api/users/${userId}`, userData),
  
  updateUserRole: (userId, role) =>
    api.put(`/api/users/${userId}/role`, { role }),
  
  deleteUser: (userId) =>
    api.delete(`/api/users/${userId}`),
  
  getUser: (userId) =>
    api.get(`/api/users/${userId}`),

  // Client assignment methods
  getUserClients: (userId) =>
    api.get(`/api/users/${userId}/clients`),
  
  updateUserClients: (userId, clientIds) =>
    api.put(`/api/users/${userId}/clients`, { client_ids: clientIds }),
  
  addUserClient: (userId, clientId) =>
    api.post(`/api/users/${userId}/clients/${clientId}`),
  
  removeUserClient: (userId, clientId) =>
    api.delete(`/api/users/${userId}/clients/${clientId}`),
};

// Clients API
export const clientsAPI = {
  getClients: () =>
    api.get('/api/clients'),
  
  createClient: (clientData) =>
    api.post('/api/clients', clientData),
  
  updateClient: (clientId, clientData) =>
    api.put(`/api/clients/${clientId}`, clientData),
  
  deleteClient: (clientId) =>
    api.delete(`/api/clients/${clientId}`),
};

// Tasks API
export const tasksAPI = {
  getTasks: (filters = {}) => {
    return api.get('/api/tasks', { params: filters });
  },
  
  createTask: (taskData) =>
    api.post('/api/tasks', taskData),
  
  updateTask: (taskId, taskData) =>
    api.put(`/api/tasks/${taskId}`, taskData),
  
  deleteTask: (taskId) =>
    api.delete(`/api/tasks/${taskId}`),
};

// Roles API
export const rolesAPI = {
  getRoles: () =>
    api.get('/api/roles'),
};

// Setup API
export const setupAPI = {
  createSuperAdmin: (userData) =>
    api.post('/api/setup/super-admin', userData),
};

// Workflows API (for future use)
export const workflowsAPI = {
  getWorkflows: (clientId = null) => {
    const params = clientId ? { client_id: clientId } : {};
    return api.get('/api/workflows', { params });
  },
  
  getWorkflow: (workflowId) =>
    api.get(`/api/workflows/${workflowId}`),
  
  createWorkflow: (workflowData) =>
    api.post('/api/workflows', workflowData),
  
  updateWorkflow: (workflowId, workflowData) =>
    api.put(`/api/workflows/${workflowId}`, workflowData),
  
  deleteWorkflow: (workflowId) =>
    api.delete(`/api/workflows/${workflowId}`),
  
  duplicateWorkflow: (workflowId) =>
    api.post(`/api/workflows/${workflowId}/duplicate`),
  
  getWorkflowVersions: (workflowId) => 
    api.get(`/api/workflows/${workflowId}/versions`),
  
  getWorkflowVersion: (workflowId, version) => 
    api.get(`/api/workflows/${workflowId}/versions/${version}`),
  
  saveNewWorkflowVersion: (workflowId, data) => 
    api.post(`/api/workflows/${workflowId}/versions`, data),
  
  getWorkflowVersionDiff: (workflowId, version) =>
    api.get(`/api/workflows/${workflowId}/versions/${version}/diff`),

  // Context browser helpers
  getNodeContextBrowser: (workflowId, nodeId, params = {}) =>
    api.get(`/api/workflows/${workflowId}/nodes/${nodeId}/context-browser`, { params }),
};

// Modules API (for future use)
export const modulesAPI = {
  getModules: () =>
    api.get('/api/modules'),
  
  createModule: (moduleData) =>
    api.post('/api/modules', moduleData),
  
  updateModule: (moduleId, moduleData) =>
    api.put(`/api/modules/${moduleId}`, moduleData),
  
  deleteModule: (moduleId) =>
    api.delete(`/api/modules/${moduleId}`),
  
  getModuleFields: (moduleId) =>
    api.get(`/api/modules/${moduleId}/fields`),
  
  createModuleField: (moduleId, fieldData) =>
    api.post(`/api/modules/${moduleId}/fields`, fieldData),
  
  getNodeData: (workflowId, nodeId) =>
    api.get(`/api/workflows/${workflowId}/nodes/${nodeId}/data`),
  
  updateNodeData: (workflowId, nodeId, data) =>
    api.put(`/api/workflows/${workflowId}/nodes/${nodeId}/data`, data),
  
  // Admin function to sync modules from engine
  syncModules: () =>
    api.post('/api/modules/sync'),
};

// Jobs API (for future use)
export const jobsAPI = {
  getJobs: (filters = {}) =>
    api.get('/api/jobs', { params: filters }),
  
  createJob: (jobData) =>
    api.post('/api/jobs', jobData),
  
  updateJob: (jobId, jobData) =>
    api.put(`/api/jobs/${jobId}`, jobData),
  
  deleteJob: (jobId) =>
    api.delete(`/api/jobs/${jobId}`),
  
  startJob: (jobId) =>
    api.post(`/api/jobs/${jobId}/start`),
  
  stopJob: (jobId) =>
    api.post(`/api/jobs/${jobId}/stop`),
  
  restartJob: (jobId) =>
    api.post(`/api/jobs/${jobId}/restart`),
  
  getJobLogs: (jobId) =>
    api.get(`/api/jobs/${jobId}/logs`),
  
  getJobDetails: (jobId) =>
    api.get(`/api/jobs/${jobId}`),
};

// Workflow execution API
export const executionAPI = {
  executeWorkflow: (workflowId, triggerData = {}) =>
    api.post(`/api/workflows/${workflowId}/execute`, { trigger_data: triggerData }),
  
  getWorkflowJobs: (workflowId) =>
    api.get(`/api/workflows/${workflowId}/jobs`),
  
  getJobDetails: (jobId) =>
    api.get(`/api/jobs/${jobId}`),
  
  deleteJob: (jobId) =>
    api.delete(`/api/jobs/${jobId}`),
  
  forceDeleteJob: (jobId) =>
    api.delete(`/api/jobs/${jobId}/force`),
  
  getAllJobs: (clientId = null, status = null) => {
    const params = {};
    if (clientId) params.client_id = clientId;
    if (status) params.status = status;
    return api.get('/api/jobs', { params });
  },
  
  getActiveJobs: (clientId = null) => {
    const params = { status: 'running,pending' };
    if (clientId) params.client_id = clientId;
    return api.get('/api/jobs', { params });
  },
  
  getFolderWatchLogs: (workflowId, limit = 100) =>
    api.get(`/api/workflows/${workflowId}/folder-watch-logs`, { params: { limit } }),
};

// Engine monitoring API
export const engineAPI = {
  getEngineStatus: () =>
    api.get('/api/engine/status'),
};

// Configuration API
export const configAPI = {
  getConfigurations: () =>
    api.get('/api/config'),
  
  createConfiguration: (configData) =>
    api.post('/api/config', configData),
  
  updateConfiguration: (configId, configData) =>
    api.put(`/api/config/${configId}`, configData),
  
  deleteConfiguration: (configId) =>
    api.delete(`/api/config/${configId}`),
  
  getSystemSettings: () =>
    api.get('/api/config/system'),
  
  updateSystemSettings: (settings) =>
    api.put('/api/config/system', settings),
  
  // Storage configuration methods
  getStorageConfigurations: () =>
    api.get('/api/config/storage'),
  
  createStorageConfiguration: (storageData) =>
    api.post('/api/config/storage', storageData),
  
  updateStorageConfiguration: (configId, storageData) =>
    api.put(`/api/config/storage/${configId}`, storageData),
  
  deleteStorageConfiguration: (configId) =>
    api.delete(`/api/config/storage/${configId}`),
  
  testStorageConfiguration: (configId) =>
    api.post(`/api/config/storage/${configId}/test`),
};

// Storage API for workflow modules
export const storageAPI = {
  getAvailableStorageConfigs: () =>
    api.get('/api/storage-configs'),
};

// Status API for status settings management
export const statusAPI = {
  getStatusSettings: () =>
    api.get('/api/status'),
  
  createStatusSetting: (statusData) =>
    api.post('/api/status', statusData),
  
  updateStatusSetting: (key, statusData) =>
    api.put(`/api/status/${key}`, statusData),
  
  deleteStatusSetting: (key) =>
    api.delete(`/api/status/${key}`),
  
  initializeDefaultStatuses: () =>
    api.post('/api/status/initialize'),
};

// Global Context API for context management
export const contextAPI = {
  getGlobalContext: (params = {}) =>
    api.get('/api/context', { params }),
  
  createContextEntry: (contextData) =>
    api.post('/api/context', contextData),
  
  updateContextEntry: (contextId, contextData) =>
    api.put(`/api/context/${contextId}`, contextData),
  
  deleteContextEntry: (contextId) =>
    api.delete(`/api/context/${contextId}`),
  
  getClientContext: (clientId) =>
    api.get(`/api/context/client/${clientId}`),
};

export default api;
